/**
 * v40 (2026-06-08) — global fetch wrapper that injects X-Client-Platform
 * / X-Client-Version / X-Client-Build on EVERY API request, regardless
 * of which file made the fetch() call.
 *
 * v41 (2026-08-16) — adds X-Client-Runtime-Version (the shell's embedded
 * expo runtimeVersion) so the OTA-unreachable cohort is countable. See
 * CLIENT_RUNTIME_VERSION below for why app version/build can't answer it.
 *
 * Why this exists
 * ---------------
 * v39 added the header injection inside `apiClient.ts`. After deploy,
 * the admin /users page showed no telemetry. Investigation found:
 *
 *   - `apiClient.ts` is only imported by one component
 *     (DeviceConnectionManager.tsx, not in the admin bundle)
 *   - 112 files use raw `fetch()` directly — bypassing apiClient.ts
 *   - tree-shaking kept apiClient.ts out of the admin bundle entirely
 *
 * Result: every authed request hit the backend WITHOUT the X-Client-*
 * headers, so `ClientTelemetryTracker` had nothing to stamp.
 *
 * Fix (v40): patch `globalThis.fetch` once at app boot. Every fetch
 * — whether from apiClient.ts, raw fetch(), or any third-party
 * module — gets the headers added when the target URL is one of the
 * Coach Cedar API hosts. Same-origin and `api.coachcedar.com` are
 * matched; cross-origin requests to unrelated hosts (e.g. CDN, Auth0,
 * RevenueCat) are passed through unmodified to avoid leaking client
 * info to third parties.
 *
 * Cross-platform
 * --------------
 * `globalThis.fetch` is the same identifier on web (browser native)
 * and native (Expo polyfill). Installing once in `main.tsx` (web) and
 * `App.tsx` (cross-platform) covers both.
 */

import { Platform } from 'react-native';
import Constants from 'expo-constants';

// Guarded require — same pattern as services/voiceCompletion.ts. This
// module is imported at the very top of app boot (app/_layout.tsx), so
// a missing/undefined native module must degrade to "no runtime header",
// never throw. Web has no expo-updates shell at all.
let ExpoUpdates: any = null;
try {
  ExpoUpdates = require('expo-updates');
} catch {
  // expo-updates unavailable (web / bare dev) — runtime header omitted.
}

// Module-load-time resolution. These don't change within a session.
const _expoCfg: any =
  (Constants as any)?.expoConfig ?? (Constants as any)?.manifest ?? {};

const CLIENT_PLATFORM: string = Platform.OS; // 'ios' | 'android' | 'web'
const CLIENT_VERSION: string = ((_expoCfg?.version ?? '') + '').slice(0, 32);
const CLIENT_BUILD: string = (() => {
  if (Platform.OS === 'ios') {
    return ((_expoCfg?.ios?.buildNumber ?? '') + '').slice(0, 32);
  }
  if (Platform.OS === 'android') {
    const code = _expoCfg?.android?.versionCode;
    return (code != null ? code + '' : '').slice(0, 32);
  }
  // Web has no separate build — surface version as build for symmetry
  // so the admin portal renders both columns without ambiguity.
  return CLIENT_VERSION;
})();

/**
 * The SHELL's embedded expo runtime version — the OTA compatibility key.
 *
 * WHY THIS AND NOT app version / build
 * ------------------------------------
 * `eas update` publishes a bundle against a runtimeVersion. A shell whose
 * embedded runtimeVersion no longer matches any published update receives
 * NOTHING — silently, forever, with no error anywhere. App version and
 * build number do NOT determine reachability: a user can sit on app
 * 1.3.10 build 232 and still be unreachable because their shell's runtime
 * predates the channel we publish to. That is exactly how one user missed
 * a month of JS fixes (his health pipe went dark and watch push inherited
 * it) and we only found out because his build number came up in
 * conversation. Without this field the stranded cohort is uncountable.
 *
 * `Updates.runtimeVersion` is a NATIVE constant baked into the binary —
 * NOT the OTA manifest's value — so it reports the shell, which is the
 * thing that can be stranded. Same distinction utils/runtimeVersionCompare.ts
 * relies on for the Laura-class mic crash guard; see that module's header.
 *
 * Empty ⇒ header omitted entirely (web, dev, or an expo-updates that
 * doesn't answer). Absence is normal and unremarkable on the backend.
 */
const CLIENT_RUNTIME_VERSION: string = (() => {
  try {
    const rv = ExpoUpdates?.runtimeVersion;
    return typeof rv === 'string' ? rv.slice(0, 32) : '';
  } catch {
    return '';
  }
})();

/**
 * What this JavaScript bundle can do, told to the server on every API request as
 * `X-Client-Caps` (comma-separated; Codes PR-3, 2026-09-29). The server reads it with
 * services/discount_offers.terms_capable_from_headers:
 *
 *   partner_offer_terms  the partner offer sheet and the partner-code success card print a
 *                        discount's TERM ("15% off … for your first 3 months"). Without it the
 *                        server withholds a monthly discount whose term is not 12 months (an
 *                        older bundle would print "first 12 months" for it).
 *   free_month_store     the redeem modal renders a STORE PAYER's free month (free month PR-5,
 *                        2026-09-30): the `free_month_store` result (the App Store extended / Google
 *                        Play deferred the subscription — "Your next charge moves back a month"),
 *                        its `pending` state, and the store refusal set. Without it the server
 *                        refuses a payer `needs_app_update` BEFORE any store call — an older bundle
 *                        would route the unknown result to the Summer50 card.
 *   offer_replace        the partner-code success card prints `replaced_previous` ("This offer
 *                        replaces your earlier one.") — free month PR-4's replace model.
 *
 * The bundle sets it, so an OTA can ship a capability (a native build number cannot say what
 * an OTA added). NATIVE ONLY: the web bundle (the legacy app and the admin portal) calls the
 * API cross-origin, and a header the API's CORS list does not allow would fail every
 * preflight — and the web app sells nothing, so it has no term to print.
 */
export const CLIENT_CAPS: readonly string[] = ['partner_offer_terms', 'free_month_store', 'offer_replace'];
const CLIENT_CAPS_HEADER_VALUE: string =
  Platform.OS === 'ios' || Platform.OS === 'android' ? CLIENT_CAPS.join(',') : '';

// API hosts we control. The wrapper only adds headers when the fetch
// target matches one of these — avoids leaking client info to Auth0,
// RevenueCat, third-party CDNs, etc.
const COACH_CEDAR_API_HOSTS = new Set([
  'api.coachcedar.com',
  'api-staging.coachcedar.com',
]);

function _shouldAttach(url: string | URL | Request): boolean {
  try {
    let href: string;
    if (typeof url === 'string') {
      href = url;
    } else if (url instanceof URL) {
      href = url.href;
    } else if (typeof Request !== 'undefined' && url instanceof Request) {
      href = url.url;
    } else {
      // Unknown shape — default to attach (same-origin paths often
      // arrive as strings starting with '/').
      return true;
    }
    // Relative paths (e.g. '/api/admin/users/all') are same-origin —
    // always attach.
    if (href.startsWith('/')) return true;
    // Absolute URLs — only attach for Coach Cedar API hosts.
    const u = new URL(href, (globalThis as any)?.location?.href || 'http://localhost');
    return COACH_CEDAR_API_HOSTS.has(u.hostname)
      || u.hostname === (globalThis as any)?.location?.hostname;
  } catch {
    return false;
  }
}

/**
 * The X-Client-* headers this app sends to the Coach Cedar API, as a plain object — exactly
 * what the fetch wrapper below attaches (jest pins the two as equal). For request paths the
 * wrapper cannot see because they do not use `fetch`: the native chat stream is
 * react-native-sse over XMLHttpRequest (hooks/useChatLogic.ts handleMobileStreaming), and
 * without these headers the server never learns the build on /api/chat (review of the FE
 * single shot, M-1: the J15 cap strip's build check never passed). Empty values are left
 * out, and X-Client-Caps is native only (CLIENT_CAPS). Pure: module constants only.
 */
export function clientTelemetryHeaderValues(): Record<string, string> {
  const out: Record<string, string> = {};
  if (CLIENT_PLATFORM) out['X-Client-Platform'] = CLIENT_PLATFORM;
  if (CLIENT_VERSION) out['X-Client-Version'] = CLIENT_VERSION;
  if (CLIENT_BUILD) out['X-Client-Build'] = CLIENT_BUILD;
  if (CLIENT_RUNTIME_VERSION) out['X-Client-Runtime-Version'] = CLIENT_RUNTIME_VERSION;
  if (CLIENT_CAPS_HEADER_VALUE) out['X-Client-Caps'] = CLIENT_CAPS_HEADER_VALUE;
  return out;
}

// Idempotency guard — installing twice would chain wrappers and double
// the work. Track on globalThis so HMR reloads don't pile up either.
const INSTALL_FLAG = '__coachCedarFetchWrapperInstalled_v40';

export function installClientTelemetryHeaders(): void {
  const g: any = globalThis as any;
  if (g[INSTALL_FLAG]) return;
  if (typeof g.fetch !== 'function') return; // SSR / unsupported env

  const original: typeof fetch = g.fetch.bind(globalThis);

  g.fetch = (input: any, init?: RequestInit): Promise<Response> => {
    try {
      if (!_shouldAttach(input)) {
        return original(input, init);
      }
      // Build/merge headers. Preserve any headers the caller set.
      const headers = new Headers(init?.headers);
      // Only set if not already present so a caller can override (e.g.
      // tests). Coach Cedar callers never set these directly.
      if (CLIENT_PLATFORM && !headers.has('X-Client-Platform')) {
        headers.set('X-Client-Platform', CLIENT_PLATFORM);
      }
      if (CLIENT_VERSION && !headers.has('X-Client-Version')) {
        headers.set('X-Client-Version', CLIENT_VERSION);
      }
      if (CLIENT_BUILD && !headers.has('X-Client-Build')) {
        headers.set('X-Client-Build', CLIENT_BUILD);
      }
      // Omitted when empty (web / dev) — the backend treats absence as
      // normal and never branches on it.
      if (CLIENT_RUNTIME_VERSION && !headers.has('X-Client-Runtime-Version')) {
        headers.set('X-Client-Runtime-Version', CLIENT_RUNTIME_VERSION);
      }
      // What this bundle can do (CLIENT_CAPS above). Native only; a caller's own value wins.
      if (CLIENT_CAPS_HEADER_VALUE && !headers.has('X-Client-Caps')) {
        headers.set('X-Client-Caps', CLIENT_CAPS_HEADER_VALUE);
      }
      return original(input, { ...(init || {}), headers });
    } catch {
      // Any failure in the wrapper falls through to the original fetch —
      // telemetry is best-effort, never break a real request.
      return original(input, init);
    }
  };

  g[INSTALL_FLAG] = true;
}
